import React, { useEffect, useState } from 'react';
import {
	Box,
	Card,
	Chip,
	Grid,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Paper,
	TablePagination,
	Skeleton,
} from '@mui/material';

import HistoryIcon from '@mui/icons-material/History';
import PieChartIcon from '@mui/icons-material/PieChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MainGrid from '../../components/MainGrid';
import { toast } from 'react-toastify';
import {
	fetchAllCarePlanData,
	fetchCarePlanById,
	fetchDashboardData,
	fetchElder,
	fetchElderCareNotes,
	fetchElderDataFile,
	fetchElderFiles,
	fetchExternalElder,
	filterCarePlanStat,
} from '../../services/ElderService';
import { getAccessToken, getElderHomeId } from '../../Utils/AuthUtil';
import { dateConverter } from '../../Utils/Helpers';
import { border, dark, teal } from '../../assets/theme/Colors';

const stats = [
	{
		label: 'AI Generations (this month)',
		key: 'total',
		sub: '28% vs last month',
		accent: true,
	},
	{
		label: 'Care Plans Generated',
		key: 'icp',
		sub: '12 more than last month',
	},
	{
		label: 'Optimized',
		key: 'ocp',
		sub: '12 more than last month',
	},
	{
		label: 'Care notes',
		key: 'cn',
		sub: '12 more than last month',
	},
];

const MODULE_COLORS = {
	'Initial Care Plan': '#0a5050',
	'Optimized Care Plan': '#1a508a',
	'Care Note': '#7c5200',
	Summary: '#2d7a4a',
};

const STATUS_COLORS = {
	Saved: '#2d7a4a',
	Pending: '#7c5200',
	Downloaded: '#2d7a4a',
};

const modules = [
	{ label: 'Summaries', key: 'sm' },
	{ label: 'Care Plans', key: 'icp' },
	{ label: 'Optimizations', key: 'ocp' },
	{ label: 'Care Notes', key: 'cn' },
];

const FILE_ITEM_FILTER = [
	'All',
	'Onboarding',
	'Initial Care Plan',
	'Optimized Care Plan',
];

const StatCard = ({ item, metric, isLoading }) => {
	return (
		<Card
			elevation={0}
			sx={{
				p: 2,
				borderRadius: 4,

				background: item.accent
					? 'linear-gradient(180deg,#e6f9f7 0%,#f5fffe 100%)'
					: '#fff',
				position: 'relative',
				overflow: 'hidden',
				border: `1px solid ${item.accent ? '#b3ede8' : border}`,
			}}>
			<Typography
				sx={{
					fontSize: 11,
					fontWeight: 600,
					color: '#6b8888',
					textTransform: 'uppercase',
					letterSpacing: 0.6,
					mb: isLoading ? 0 : 1,
				}}>
				{item.label}
			</Typography>

			{isLoading ? (
				<Skeleton variant="text" width={32} height={42} />
			) : (
				<Typography
					sx={{
						fontSize: 26,
						fontWeight: 700,
						lineHeight: 1,
						color: dark,
					}}>
					{metric}
				</Typography>
			)}

			{/* <Stack direction="row" spacing={0.5} alignItems="center" mt={1.5}>
				<TrendingUpIcon sx={{ fontSize: 14, color: '#2d7a4a' }} />

				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 500,
						color: '#2d7a4a',
					}}>
					{item.sub}
				</Typography>
			</Stack> */}
		</Card>
	);
};

function SystemUsageDashboard() {
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const [metrics, setMetrics] = useState({
		cn: 0,
		icp: 0,
		ocp: 0,
		sm: 0,
		total: 0,
	});
	const [careStatFrequency, setCareStatFrequency] = useState({
		cn: 0,
		icp: 0,
		ocp: 0,
		sm: 0,
	});
	const [allData, setAllData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(4);
	const [page, setPage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		onFetchAndStructureAllData();
	}, []);

	async function onFetchAndStructureAllData() {
		setIsLoading(true);
		const planData = await fetchAllCarePlanData(homeId, token);

		const date = new Date();
		const startDate = new Date(
			date.getFullYear(),
			date.getMonth(),
			1,
		).toISOString();
		const endDate = new Date(
			date.getFullYear(),
			date.getMonth() + 1,
			0,
		).toISOString();

		const currentMonthData = await filterCarePlanStat(
			homeId,
			token,
			startDate,
			endDate,
		);

		const counts = currentMonthData.counts;
		onSetGraphData(counts);

		setAllData(planData);
		setIsLoading(false);
	}

	function onSetGraphData(counts) {
		const cnSize = counts.cn;
		const ipSize = counts.icp;
		const opSize = counts.ocp;
		const smSize = counts.sm ?? 0;
		const totalData = cnSize + ipSize + opSize + smSize;

		setMetrics({
			cn: cnSize,
			icp: ipSize,
			ocp: opSize,
			sm: smSize,
			total: totalData,
		});

		const calcPercent = (size, total) =>
			total > 0 ? ((size / total) * 100).toFixed(0) : 0;

		const cnFreq = calcPercent(cnSize, totalData);
		const icpFreq = calcPercent(ipSize, totalData);
		const ocpFreq = calcPercent(opSize, totalData);
		const smFreq = calcPercent(smSize, totalData);

		const totalFreq =
			parseFloat(cnFreq) +
			parseFloat(icpFreq) +
			parseFloat(ocpFreq) +
			parseFloat(smFreq);

		let cn = parseFloat(cnFreq);
		let icp = parseFloat(icpFreq);
		let ocp = parseFloat(ocpFreq);
		let sm = parseFloat(smFreq);

		if (totalData > 0 && totalFreq !== 100) {
			const diff = 100 - totalFreq;
			const maxVal = Math.max(cn, icp, ocp);
			if (cn === maxVal) cn += diff;
			else if (icp === maxVal) icp += diff;
			else ocp += diff;
		}

		setCareStatFrequency({ cn, icp, ocp, sm });
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const getModuleColor = module => MODULE_COLORS[module] ?? '#6b8888';
	const getStatusColor = status => STATUS_COLORS[status] ?? '#6b8888';

	return (
		<MainGrid>
			{/* HEADER */}
			<Box mb={3}>
				<Typography
					sx={{
						fontSize: '22px',
						fontWeight: 600,
						color: dark,
						mb: 0.5,
					}}>
					System Usage Dashboard
				</Typography>

				<Typography
					sx={{
						fontSize: 14,
						color: '#6b8888',
					}}>
					Track AI module usage across the organization by date and
					resident
				</Typography>
			</Box>

			{/* STATS */}
			<Grid container spacing={2} mb={3}>
				{stats.map((item, index) => (
					<Grid item xs={12} sm={6} lg={3} key={index}>
						<StatCard
							item={item}
							metric={metrics[item.key]}
							isLoading={isLoading}
						/>
					</Grid>
				))}
			</Grid>

			{/* CONTENT */}
			<Grid container spacing={2}>
				{/* RECENT ACTIVITY */}
				<Grid item xs={12} lg={7}>
					<Card
						elevation={0}
						sx={{
							borderRadius: 4,
							border: '1px solid #d8e8e8',
							p: 2.5,
						}}>
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							mb={2}>
							<HistoryIcon
								sx={{ color: teal, width: 16, height: 16 }}
							/>

							<Typography
								sx={{
									fontSize: 13,
									fontWeight: 600,
									color: dark,
								}}>
								Recent Activity
							</Typography>
						</Stack>

						<TableContainer component={Paper} elevation={0}>
							<Table sx={{ tableLayout: 'fixed', width: '100%' }}>
								<TableHead>
									<TableRow>
										<TableCell
											sx={{
												fontSize: '11px',
												fontWeight: 600,
												color: '#6b8888',
											}}>
											Module
										</TableCell>
										<TableCell
											sx={{
												fontSize: '11px',
												fontWeight: 600,
												color: '#6b8888',
											}}>
											Resident
										</TableCell>
										<TableCell
											sx={{
												fontSize: '11px',
												fontWeight: 600,
												color: '#6b8888',
											}}>
											User
										</TableCell>
										<TableCell
											sx={{
												fontSize: '11px',
												fontWeight: 600,
												color: '#6b8888',
											}}>
											Date
										</TableCell>
										<TableCell
											sx={{
												fontSize: '11px',
												fontWeight: 600,
												color: '#6b8888',
											}}>
											Status
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{isLoading ? (
										<TableRow>
											{[...Array(5)].map((_, index) => (
												<TableCell
													key={index}
													align="center">
													<Skeleton
														animation="wave"
														style={{
															borderRadius: '5px',
															height: 20,
														}}
														variant="rounded"
													/>
												</TableCell>
											))}
										</TableRow>
									) : allData.length > 0 ? (
										allData
											.slice(
												page * rowsPerPage,
												page * rowsPerPage +
													rowsPerPage,
											)
											.map((row, index) => (
												<TableRow key={index} hover>
													<TableCell>
														<Chip
															label={row.type}
															size="small"
															sx={{
																background: `${getModuleColor(row.type)}20`,
																color: getModuleColor(
																	row.type,
																),
																fontSize:
																	'12px',
															}}
														/>
													</TableCell>
													<TableCell
														sx={{
															fontSize: '13px',
														}}>
														{row.elderName}
													</TableCell>
													<TableCell
														sx={{
															fontSize: '13px',
														}}>
														{row.admin}
													</TableCell>
													<TableCell
														sx={{
															fontSize: '13px',
														}}>
														{dateConverter(
															row.addedDate,
														)}
													</TableCell>
													<TableCell>
														<Chip
															label={'Saved'}
															size="small"
															sx={{
																background: `${getStatusColor('Saved')}20`,
																color: getStatusColor(
																	'Saved',
																),
																fontSize:
																	'12px',
															}}
														/>
													</TableCell>
												</TableRow>
											))
									) : (
										<TableRow>
											<TableCell
												colSpan={5}
												align="center">
												<Typography fontSize={13}>
													No data available
												</Typography>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[]}
							component="div"
							count={allData ? allData.length : 0}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Card>
				</Grid>

				{/* MODULE BREAKDOWN */}
				<Grid item xs={12} lg={5}>
					<Card
						elevation={0}
						sx={{
							borderRadius: 4,
							border: '1px solid #d8e8e8',
							p: 2.5,
						}}>
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							mb={3}>
							<PieChartIcon
								sx={{ color: teal, width: 16, height: 16 }}
							/>

							<Typography
								sx={{
									fontSize: 13,
									fontWeight: 600,
									color: dark,
								}}>
								Module Breakdown
							</Typography>
						</Stack>

						<Stack spacing={3}>
							{modules.map((item, index) => (
								<Box key={index}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
										mb={1}>
										<Typography
											sx={{
												fontSize: 13,
												fontWeight: 500,
												color: '#1a2424',
											}}>
											{item.label}
										</Typography>

										<Typography
											sx={{
												fontSize: 13,
												fontWeight: 600,
												color: dark,
											}}>
											{metrics[item.key]} (
											{careStatFrequency[item.key]}%)
										</Typography>
									</Stack>

									<LinearProgress
										variant="determinate"
										value={careStatFrequency[item.key]}
										sx={{
											height: 8,
											borderRadius: 999,
											background: '#eaf3f3',
											'& .MuiLinearProgress-bar': {
												borderRadius: 999,
												backgroundColor: teal,
											},
										}}
									/>
								</Box>
							))}
						</Stack>
					</Card>
				</Grid>
			</Grid>
		</MainGrid>
	);
}

export default SystemUsageDashboard;
